import React from 'react';
import { findWithUUID } from '@/utils/uuidHelper';
import findResult from '@/utils/resultsHelper';
import { isSplitUrl, joinUrl, splitUrl } from '@/constants/urls';
import { states } from '@/constants/lifecycle';
import InEditCell from '@/components/table/InEditCell';
import {
  showDictionaryField,
  updateDictionaryFields,
  updateDictionaryFieldType,
} from '../../dictionaries/actions';
import { canEditDataset, datasetIsEditing } from '../../helper';
import { datasetPerms } from '../../sharing/state/state';
import { resolveCatalogueTemplate } from '../../catalogue/helper';

const DictionaryFieldContainer = ({
  exists,
  canRead,
  children,
  colSpan,
}) => (
  <InEditCell exists={exists} canRead={canRead} colSpan={colSpan}>
    {children}
  </InEditCell>
);

const accessControl = (role, exists, canEdit) => (
  {
    exists,
    canEdit: exists ? canEdit : false,
    canRead: exists ? role.permissions.datasets.read : false,
  }
);

const withPmnFunctions = (pmnFunctions, defaultPmnRuleCode) => (
  {
    pmnFunctions: pmnFunctions.lookups.results || [],
    pmnFunctionsReadStatus: pmnFunctions.lookups.readStatus || states.NONE,
    defaultPmnRuleCode,
  }
);

const withoutItem = (role, canEdit, pmnFunctions, defaultPmnRuleCode) => (
  {
    ...accessControl(role, false, canEdit),
    ...withPmnFunctions(pmnFunctions, defaultPmnRuleCode),
  }
);

const withItem = (dictionary, item, role, isEditing, canEdit, pmnFunctions, defaultPmnRuleCode, template) => {
  const constraint = item.constraints;
  const lookups = dictionary.lookups || {};

  const uri = isSplitUrl(item.uri) ? item.uri : splitUrl(item.uri);

  return (
    {
      ...accessControl(role, true, canEdit),
      isEditing,
      template,
      type: item.type,
      label: item.label,
      name: item.name,
      description: item.description,
      uri: joinUrl(uri),
      uriProtocol: uri.protocol,
      uriLink: uri.link,
      constraint,
      lookups,
      hasLookup: !!lookups[constraint],
      pmnRule: item.pseudonymisation_rule,
      entity: item.entity,
      cohortFilter: item.cohort_filter,
      ...withPmnFunctions(pmnFunctions, defaultPmnRuleCode),
    }
  );
};

export const mapStateToProps = ({
  datasets: { datasets },
  catalogues: { templates },
  pmnFunctions: { pmnFunctions },
  auth: { role },
  configurations: { configurations },
}, ownProps) => {
  const { dataset, template } = resolveCatalogueTemplate(datasets, templates, ownProps, {
    dictionaries: [],
    permissions: datasetPerms(),
    isEditing: false,
  });
  const dictionary = findWithUUID(dataset.dictionaries, ownProps.dictionaryUUID) || { fields: [] };
  const item = findWithUUID(dictionary.fields, ownProps.fieldUUID);
  const canEdit = canEditDataset(dataset, role);
  const pmnOperation = findResult(configurations, 'all', 'default-pmn-operation').value || { value: null };

  return item
    ? withItem(dictionary, item, role, datasetIsEditing(dataset), canEdit, pmnFunctions, pmnOperation.value, template)
    : withoutItem(role, canEdit, pmnFunctions, pmnOperation.value);
};

export const mapDispatchToProps = {
  showFieldDialog: showDictionaryField,
  updateProperties: updateDictionaryFields,
  updateType: updateDictionaryFieldType,
};

export default DictionaryFieldContainer;
